import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Card } from './Card';

/** Card with a title row and an optional source line. */
export const Section: React.FC<{
  title: string; sub?: React.ReactNode; source?: string; actions?: React.ReactNode; children: React.ReactNode; className?: string;
}> = ({ title, sub, source, actions, children, className = '' }) => (
  <Card className={`min-w-0 ${className}`}>
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h2 className="text-[14px] font-semibold text-stone-900 tracking-[-0.01em]">{title}</h2>
        {sub && <p className="text-[12.5px] text-stone-500 mt-0.5">{sub}</p>}
      </div>
      {actions && <div className="shrink-0 flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
    {children}
    {source && <p className="text-[11px] text-stone-400 mt-3">Source: {source}</p>}
  </Card>
);

/** A failed query renders red, never as zeros. */
export const ErrorPanel: React.FC<{ error: unknown; label?: string }> = ({ error, label }) => (
  <div role="alert" className="p-3 rounded-md bg-rose-50 border border-rose-200 flex items-start gap-2 break-words">
    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
    <div className="text-[12.5px] text-rose-700">
      {label && <span className="font-semibold">{label}: </span>}
      {(error as Error)?.message ?? String(error)}
    </div>
  </div>
);

export const LoadingRow: React.FC<{ label?: string }> = ({ label }) => (
  <div role="status" className="flex items-center gap-2 py-8 justify-center text-stone-500">
    <Loader2 className="w-4 h-4 animate-spin" />
    <span className="text-[12.5px]">{label ?? 'Loading'}</span>
  </div>
);

export const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[12.5px] text-stone-400 py-4 text-center">{children}</p>
);

/** Truncation is always visible: shown when a list hits its fetch cap. */
export const CapNote: React.FC<{ shown: number; cap: number }> = ({ shown, cap }) =>
  shown >= cap ? (
    <p className="text-[12px] text-amber-700 mt-2">Showing the newest {cap} rows. Narrow the filters for the rest.</p>
  ) : null;

/** Every user mention is a link into the user page. */
export const UserLink: React.FC<{ id: string | null | undefined; label?: string | null }> = ({ id, label }) => {
  if (!id) return <span className="text-stone-400">anonymous</span>;
  return (
    <Link to={`/users/${id}`} className="text-indigo-600 hover:text-indigo-800 font-medium break-words">
      {label || id.slice(0, 8)}
    </Link>
  );
};

/** Definition-list row. */
export const KV: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-stone-100 last:border-0">
    <dt className="text-[12.5px] text-stone-500 shrink-0">{k}</dt>
    <dd className="text-[12.5px] text-stone-800 text-right break-words min-w-0">{children}</dd>
  </div>
);

/** Table scaffolding: the page body never scrolls sideways, tables do. */
export const TableWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="overflow-x-auto -mx-1 px-1">
    <table className="w-full text-[13px]">{children}</table>
  </div>
);

export const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`text-left font-medium text-[12px] text-stone-500 py-2 pr-4 whitespace-nowrap border-b border-stone-200 ${className}`}>{children}</th>
);

export const Td: React.FC<{ children?: React.ReactNode; className?: string; colSpan?: number }> = ({ children, className = '', colSpan }) => (
  <td colSpan={colSpan} className={`py-2 pr-4 border-b border-stone-100 align-top text-stone-700 ${className}`}>{children}</td>
);

/** Horizontal bars for a ranked list. */
export const Bars: React.FC<{ rows: { label: React.ReactNode; n: number; key?: string; extra?: React.ReactNode }[]; of?: number }> = ({ rows, of }) => {
  const max = of ?? Math.max(1, ...rows.map(r => r.n));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={r.key ?? i} className="flex items-center gap-3">
          <span className="w-48 shrink-0 break-words leading-snug text-[12.5px] text-stone-700">{r.label}</span>
          <div className="flex-1 h-2 bg-stone-100 rounded-sm overflow-hidden">
            <div className="h-full bg-indigo-600 rounded-sm" style={{ width: `${max > 0 ? Math.max(1.5, (r.n / max) * 100) : 0}%` }} />
          </div>
          <span className="w-12 text-right text-[12.5px] tabular-nums text-stone-800">{r.n.toLocaleString('en-GB')}</span>
          {r.extra}
        </div>
      ))}
    </div>
  );
};

/** Segmented control (filters). */
export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label?: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex items-center rounded-md border border-stone-200 bg-white p-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={[
            'h-7 px-2.5 rounded-[5px] text-[12.5px] font-medium transition-colors whitespace-nowrap',
            value === o.value ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Newer / older pager with the real total. */
export const Pager: React.FC<{ page: number; per: number; total: number; onPage: (p: number) => void }> = ({ page, per, total, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / per));
  const from = total === 0 ? 0 : (page - 1) * per + 1;
  const to = Math.min(total, page * per);
  return (
    <div className="flex items-center justify-between mt-4 text-[12.5px] text-stone-500">
      <span className="tabular-nums">{from.toLocaleString('en-GB')} to {to.toLocaleString('en-GB')} of {total.toLocaleString('en-GB')}</span>
      <div className="flex gap-2">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="h-8 px-3 rounded-md border border-stone-200 bg-white text-stone-700 disabled:opacity-40 hover:border-stone-300">Previous</button>
        <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="h-8 px-3 rounded-md border border-stone-200 bg-white text-stone-700 disabled:opacity-40 hover:border-stone-300">Next</button>
      </div>
    </div>
  );
};

/** UK/US marker. */
export const MarketTag: React.FC<{ market: string | null | undefined }> = ({ market }) => (
  <span className={`inline-flex items-center gap-1 text-[12px] font-medium ${market === 'us' ? 'text-teal-700' : 'text-indigo-700'}`}>
    <span className={`w-1.5 h-1.5 rounded-sm ${market === 'us' ? 'bg-us' : 'bg-uk'}`} />{market === 'us' ? 'US' : 'UK'}
  </span>
);
